import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { X, ChevronDown } from 'lucide-react';
import { fetchHomeDebates, fetchDebateLiveStatus } from '../services/petitionService';
import { isDebateLive, UKP_LIVE_URL } from '../utils/debateTime';
import { buildAppPetitionUrl } from '../utils/urlUtils';

/* Dismissal is remembered per debate date, so closing today's bar does not
   hide the next one. Kept in sessionStorage: a debate runs for a few hours,
   and the bar should come back on a later visit if it is still on air. */
const DISMISS_KEY = 'ukpt_live_bar_dismissed';

const isDismissed = (key) => {
    try { return sessionStorage.getItem(DISMISS_KEY) === key; } catch { return false; }
};
const remember = (key) => {
    try { sessionStorage.setItem(DISMISS_KEY, key); } catch { /* private mode */ }
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
`;

const slideDown = keyframes`
  from { transform: translateY(-100%); }
  to   { transform: translateY(0); }
`;

const Bar = styled.div`
  position: sticky;
  top: 0;
  z-index: 1200;
  background: linear-gradient(90deg, #7f1d1d 0%, #b91c1c 55%, #dc2626 100%);
  color: #fff;
  animation: ${slideDown} 0.25s ease;
`;

const Row = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 9px 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) { padding: 8px 10px; gap: 9px; }
`;

const Dot = styled.span`
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #fff;
  animation: ${pulse} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Label = styled.span`
  flex-shrink: 0;
  font-size: 0.72em;
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
`;

const Text = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.88em;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a { color: #fff; text-decoration: underline; text-underline-offset: 2px; }

  @media (max-width: 640px) { font-size: 0.8em; }
`;

const Watch = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-family: inherit;
  font-size: 0.8em;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(255, 255, 255, 0.3); }

  svg { width: 13px; height: 13px; transition: transform 0.2s; }
  svg.open { transform: rotate(180deg); }
`;

const Close = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.2); color: #fff; }
  svg { width: 15px; height: 15px; }
`;

const Player = styled.div`
  background: #000;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const Frame = styled.div`
  max-width: 780px;
  margin: 0 auto;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: min(56vh, 420px);

  iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
`;

const Caveat = styled.p`
  max-width: 780px;
  margin: 0 auto;
  padding: 8px 14px 12px;
  font-size: 0.76em;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);

  a { color: rgba(255, 255, 255, 0.92); }
`;

const LiveDebateBar = () => {
    const [debate, setDebate] = useState(null);
    const [open, setOpen] = useState(false);
    const [closed, setClosed] = useState(false);
    const [videoId, setVideoId] = useState(null);

    // Find a petition whose debate window is open right now. The home debates
    // endpoint is already cached and used elsewhere, so this adds no new load.
    useEffect(() => {
        let cancelled = false;

        // ?livebar=1 previews the bar outside a real debate window, using the
        // next scheduled debate. Development only — ignored in production, so
        // it cannot be triggered by a visitor.
        // Gated on hostname rather than NODE_ENV: the local build is compiled
        // with NODE_ENV=production too, so an env check would strip this from
        // the very build it is meant to help preview.
        const preview = typeof window !== 'undefined'
            && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
            && new URLSearchParams(window.location.search).get('livebar') === '1';

        const check = () => {
            fetchHomeDebates()
                .then(list => {
                    if (cancelled) return;
                    const rows = list || [];
                    const live = rows.find(p => isDebateLive(p.scheduledDebateDate))
                        || (preview ? rows[0] : null);
                    setDebate(live || null);
                    if (live && !preview && isDismissed(live.scheduledDebateDate)) setClosed(true);
                })
                .catch(() => { /* the bar is optional; never surface an error */ });
        };

        check();
        // A debate can start while someone is already on the page.
        const id = setInterval(check, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Only ask which video is streaming once we know a debate is on.
    useEffect(() => {
        if (!debate) return undefined;
        let cancelled = false;
        fetchDebateLiveStatus().then(r => {
            if (!cancelled && r && r.live && r.videoId) setVideoId(r.videoId);
        });
        return () => { cancelled = true; };
    }, [debate]);

    const dismiss = useCallback(() => {
        setClosed(true);
        setOpen(false);
        if (debate) remember(debate.scheduledDebateDate);
    }, [debate]);

    if (!debate || closed) return null;

    const href = buildAppPetitionUrl(debate.id || debate.petition_id, debate.title);
    const src = videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;

    return (
        <Bar role="region" aria-label="Live parliamentary debate">
            <Row>
                <Dot aria-hidden="true" />
                <Label>Live</Label>
                <Text>
                    Debating now in Parliament:{' '}
                    <Link to={href}>{debate.title}</Link>
                </Text>
                <Watch
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                >
                    {open ? 'Hide' : 'Watch'}
                    <ChevronDown className={open ? 'open' : ''} aria-hidden="true" />
                </Watch>
                <Close type="button" onClick={dismiss} aria-label="Dismiss live debate banner">
                    <X aria-hidden="true" />
                </Close>
            </Row>

            {open && (
                <Player>
                    {src ? (
                        <Frame>
                            <iframe
                                src={src}
                                title="UK Parliament live stream"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </Frame>
                    ) : (
                        <Caveat>
                            The stream could not be identified just now.{' '}
                            <a href={UKP_LIVE_URL} target="_blank" rel="noopener noreferrer">
                                Watch on YouTube
                            </a>
                        </Caveat>
                    )}
                    {/* Parliament's channel also carries committees and other
                        chamber business, so the stream may not be this debate. */}
                    {src && <Caveat>
                        Live from UK Parliament. Coverage may switch between the
                        chamber, Westminster Hall and committees.{' '}
                        <a href={UKP_LIVE_URL} target="_blank" rel="noopener noreferrer">
                            Open on YouTube
                        </a>
                    </Caveat>}
                </Player>
            )}
        </Bar>
    );
};

export default LiveDebateBar;
